import React from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import { Bold, Italic, Underline, List, ListOrdered, Quote, Heading1, Heading2, Link2, Image as ImageIcon, Undo2, Redo2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * RichText — TipTap-powered editor.
 * `value` = HTML string, `onChange(html)` fires on every edit.
 */
export default function RichText({ value = "", onChange, placeholder = "Write article body…", testId = "rich-text" }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        link: { openOnClick: false, autolink: true, linkOnPaste: true },
      }),
      Image.configure({ inline: false, allowBase64: false }),
      Placeholder.configure({ placeholder }),
    ],
    content: value,
    onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
    editorProps: {
      attributes: {
        class: "prose max-w-none min-h-[240px] p-4 focus:outline-none",
        "data-testid": testId,
      },
    },
  });

  if (!editor) return null;

  const uploadInline = async () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = async (e) => {
      const f = e.target.files?.[0];
      if (!f) return;
      const fd = new FormData();
      fd.append("file", f);
      fd.append("folder", "article-inline");
      try {
        const { data } = await api.post("/admin/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        editor.chain().focus().setImage({ src: data.url, alt: f.name }).run();
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Upload failed");
      }
    };
    input.click();
  };

  const btn = "h-8 w-8 grid place-items-center rounded-lg border border-[#E5E7EB] bg-white hover:bg-[#F3F6FA] text-[#1F2937] data-[active=true]:bg-[#2563EB] data-[active=true]:text-white data-[active=true]:border-[#2563EB]";

  return (
    <div className="rounded-2xl border border-[#E5E7EB] bg-white overflow-hidden" data-testid={`${testId}-wrap`}>
      <div className="flex flex-wrap items-center gap-1 px-3 py-2 border-b border-[#E5E7EB] bg-[#F8FAFC]">
        <button type="button" data-testid={`${testId}-h1`} data-active={editor.isActive("heading", { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} className={btn}><Heading1 className="w-4 h-4"/></button>
        <button type="button" data-testid={`${testId}-h2`} data-active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} className={btn}><Heading2 className="w-4 h-4"/></button>
        <span className="w-px h-5 bg-[#E5E7EB] mx-1"/>
        <button type="button" data-testid={`${testId}-bold`} data-active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()} className={btn}><Bold className="w-4 h-4"/></button>
        <button type="button" data-testid={`${testId}-italic`} data-active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()} className={btn}><Italic className="w-4 h-4"/></button>
        <button type="button" data-testid={`${testId}-strike`} data-active={editor.isActive("strike")} onClick={() => editor.chain().focus().toggleStrike().run()} className={btn}><Underline className="w-4 h-4"/></button>
        <span className="w-px h-5 bg-[#E5E7EB] mx-1"/>
        <button type="button" data-testid={`${testId}-ul`} data-active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()} className={btn}><List className="w-4 h-4"/></button>
        <button type="button" data-testid={`${testId}-ol`} data-active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()} className={btn}><ListOrdered className="w-4 h-4"/></button>
        <button type="button" data-testid={`${testId}-quote`} data-active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()} className={btn}><Quote className="w-4 h-4"/></button>
        <span className="w-px h-5 bg-[#E5E7EB] mx-1"/>
        <button type="button" data-testid={`${testId}-link`} onClick={() => {
          const url = window.prompt("URL", editor.getAttributes("link").href || "https://");
          if (url === null) return;
          if (url === "") return editor.chain().focus().unsetLink().run();
          editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
        }} className={btn}><Link2 className="w-4 h-4"/></button>
        <button type="button" data-testid={`${testId}-image`} onClick={uploadInline} className={btn}><ImageIcon className="w-4 h-4"/></button>
        <span className="w-px h-5 bg-[#E5E7EB] mx-1"/>
        <button type="button" data-testid={`${testId}-undo`} onClick={() => editor.chain().focus().undo().run()} className={btn}><Undo2 className="w-4 h-4"/></button>
        <button type="button" data-testid={`${testId}-redo`} onClick={() => editor.chain().focus().redo().run()} className={btn}><Redo2 className="w-4 h-4"/></button>
      </div>
      <EditorContent editor={editor}/>
    </div>
  );
}
